
import * as _ from "lodash";
import "es6-promise";

import axios, { AxiosResponse } from "axios";
import Vue, { ComponentOptions } from "vue";
import Component from "vue-class-component";
import {
  Emit,
  Inject,
  Model,
  Prop,
  Provide,
  Watch,
} from "vue-property-decorator";

import { State, Getter, Action, Mutation, namespace } from "vuex-class";

import { Sample, SampleCartItems } from "../model";
import { SET_SAMPLES } from "../store";
import { WebAPI } from "../web-api";

import { DummySampleList as _dummySampleList } from "../test-data";
import { Snackbar } from "../snackbar";
import CSVSampleListUpload from "./CSVSampleListUpload/CSVSampleListUpload.vue";

@Component({
  props: {},
  filters: {},
  beforeRouteLeave(to: any, from: any, next: any) {
    (this as any).beforeRouteLeave(to, from, next);
  },
})
export default class SampleCart extends Vue {
  _DEBUG: boolean = false;

  @Prop({ default: true, type: Boolean })
  public showButtons: boolean;

  @Prop({ default: true, type: Boolean })
  public showToolbar: boolean;

  @Prop({ default: true, type: Boolean })
  public showAddMenu: boolean;

  @Prop({ default: true, type: Boolean })
  public editableSetName: boolean;

  @Prop({ default: true, type: Boolean })
  public selectable: boolean;

  // = ["name", "metadata.condition", "R1", "R2"];
  @Prop({ default: () => ["name", "R1", "R2"], type: Array })
  public fields: Array<string>;

  // = ["name", "metadata.condition"];
  @Prop({ default: () => [], type: Array })
  public editableFields: Array<string>;

  public submitting: boolean = false;
  public error_alert_message: string = "Everything is fine. 🐺";
  public snackbar_message: string = "Everything is fine. ☃";
  public snackbar_duration: number = 2000;
  public csv_file: string = "";

  // For Vuex, we make 'samples' a readonly computed attribute that deep clones
  // the object held by the vuex store. 'samples' in the vuex store is
  // only modified by wholesale replacement upon 'Save' or navigation
  // away from the SampleCart route.
  // public samples: Sample[];

  public selectedSamples: Sample[] = [];

  async beforeCreate() {
    // Example data for debug only
    if (this._DEBUG) {
      if (
        this.$store.getters.sample_cart_count === null ||
        this.$store.getters.sample_cart_count === 0
      ) {
        const s: SampleCartItems = _.cloneDeep(_dummySampleList);
        this.$store.commit(SET_SAMPLES, s);
      }
    }
  }

  created() {
    this._samples = _.cloneDeep(this.$store.state.samples);
    // fixes DOM re-render in table when editing conditions
    //  https://github.com/MonashBioinformaticsPlatform/laxy/issues/66
    this.$store.commit(SET_SAMPLES, this._samples);
  }

  public _samples: SampleCartItems;
  get samples(): SampleCartItems {
    this._samples = _.cloneDeep(this.$store.state.samples);
    return this._samples;
  }

  set samples(samplecart: SampleCartItems) {
    this._samples = samplecart;
    this.$store.commit(SET_SAMPLES, samplecart as SampleCartItems);
  }

  async submit() {
    try {
      this.submitting = true;
      await this.$store.dispatch(SET_SAMPLES, this._samples);
      this.submitting = false;
      Snackbar.flashMessage("Saved !");
    } catch (error) {
      console.log(error);
      this.submitting = false;
      this.error_alert_message = error.toString();
      this.openDialog("error_dialog");
      throw error;
    }
  }

  async submitCSV(filelist: FileList) {
    console.log(filelist);
    const file: File = filelist[0];
    const data = new FormData(); // automatically uses 'content-type': 'multipart/form-data' in axios ?
    data.set("name", this.samples.name);
    data.set("file", file);
    //const headers = {headers: {'content-type': 'multipart/form-data'}};
    try {
      this.closeDialog("csv_file_select_dialog");
      this.submitting = true;
      const response = (await WebAPI.fetcher.post(
        "/api/v1/samplecart/",
        data
      )) as AxiosResponse;
      // TODO: CSV upload doesn't append/merge, it aways creates a new SampleCart.
      //       Implement backend PATCH method so we can append/merge an uploaded CSV
      //       (or parse and merge CSV clientside - probably a bad idea since we really
      //        want to be able to validate CSV serverside before allowing it to be accepted)
      // let response = null;
      // if (this.samples.id == null) {
      //     const response = await WebAPI.fetcher.post("/api/v1/samplecart/", data) as AxiosResponse;
      //     this._samples.id = response.data.id;
      // } else {
      //     const response = await WebAPI.fetcher.patch(`/api/v1/samplecart/{this.samples.id}`, data) as AxiosResponse;
      // }
      this.submitting = false;
      Snackbar.flashMessage("Saved !");
      // coerce JSON into a list of proper Sample objects, ensuring all properties are present
      const _samples: Sample[] = _.map(response.data.samples, (s) => {
        return new Sample(s);
      });
      this.populateSelectionList(_samples);

      // TODO: Save and highlight validation issues (eg identical sample names)
      //       Route to next step
    } catch (error) {
      console.log(error);
      this.submitting = false;
      this.error_alert_message = error.toString();
      this.closeDialog("csv_file_select_dialog");
      this.openDialog("error_dialog");
      throw error;
    }
  }

  onSelect(rows: any) {
    this.selectedSamples = rows as Array<Sample>;
  }

  removeSelected() {
    for (const row of this.selectedSamples) {
      const i = this.samples.items.indexOf(row);
      this.samples.items.splice(i, 1);
    }
    this.$store.commit(SET_SAMPLES, this.samples);
  }

  save() {
    this.submit();
  }

  async saveAndContinue() {
    try {
      await this.submit();
      // TODO: We should fire an event here to let the parent component do something
      //  (eg catch event in index.ts, do toggleSidenav('rightSidenav') )
      // this.routeTo("rnasik");
    } catch (error) {
      throw error;
    }
  }

  openDialog(ref: string) {
    (this.$refs[ref] as MdDialog).open();
  }

  closeDialog(ref: string) {
    (this.$refs[ref] as MdDialog).close();
  }

  populateSelectionList(sample_list: Array<Sample>) {
    console.log(sample_list);
    this._samples.items = sample_list;
    this.$store.commit(SET_SAMPLES, this._samples);
  }

  routeTo(name: string, params: any = {}) {
    this.$router.push({ name: name, params: params });
  }

  // This ensures changes to sample table are committed to the vuex store
  // prior to navigating away. Defined here and wrapped on the Component
  // decorator so `this` works correctly.
  // https://router.vuejs.org/en/advanced/navigation-guards.html
  beforeRouteLeave(to: any, from: any, next: any) {
    this.$store.commit(SET_SAMPLES, this._samples);
    next();
  }
}
